import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { cn } from '@/components/ui/utils';
import useMerchants from '@/hooks/use-merchants';
import { Check, ChevronsUpDown, Search, Store, X } from 'lucide-react';
import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

interface MerchantPickerProps {
  selectedMerchants: string[];
  onSelectionChange: (merchants: string[]) => void;
  placeholder?: string;
  disabled?: boolean;
}

export function MerchantPicker({
  selectedMerchants,
  onSelectionChange,
  placeholder,
  disabled,
}: MerchantPickerProps) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [searchValue, setSearchValue] = useState('');
  const { merchants, isLoading } = useMerchants();

  const filteredMerchants = useMemo(() => {
    return merchants.filter(
      (name: string) =>
        name && name.toLowerCase().includes(searchValue.toLowerCase())
    );
  }, [merchants, searchValue]);

  const toggleMerchant = useCallback(
    (merchantName: string) => {
      if (selectedMerchants.includes(merchantName)) {
        onSelectionChange(selectedMerchants.filter((m) => m !== merchantName));
      } else {
        onSelectionChange([...selectedMerchants, merchantName]);
      }
    },
    [onSelectionChange, selectedMerchants]
  );

  const removeMerchant = useCallback(
    (merchantName: string) => {
      onSelectionChange(selectedMerchants.filter((m) => m !== merchantName));
    },
    [onSelectionChange, selectedMerchants]
  );

  if (isLoading) {
    return (
      <Button
        variant='outline'
        className='w-full justify-between min-h-10'
        disabled
      >
        <div className='flex items-center gap-2'>
          <Store className='h-3 w-3' />
          <span>{t('common.loading')}...</span>
        </div>
        <ChevronsUpDown className='ml-2 h-3 w-3 shrink-0 opacity-50' />
      </Button>
    );
  }

  const count = selectedMerchants.length;

  return (
    <div className='space-y-2'>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            variant='outline'
            role='combobox'
            aria-expanded={open}
            className='w-full justify-between min-h-10'
            disabled={disabled}
          >
            <span className='truncate'>
              {count > 0
                ? `${count} ${t('common.selected')}`
                : (placeholder ?? t('merchantPicker.selectMerchants'))}
            </span>
            <ChevronsUpDown className='ml-2 h-4 w-4 shrink-0 opacity-50' />
          </Button>
        </PopoverTrigger>
        <PopoverContent
          className='w-[280px] p-0 max-h-[300px] flex flex-col'
          align='start'
          usePortal={false}
        >
          <div className='flex items-center border-b px-3 shrink-0'>
            <Search className='mr-2 h-4 w-4 shrink-0 opacity-50' />
            <Input
              placeholder={t('merchantPicker.searchMerchants')}
              value={searchValue}
              onChange={(e) => setSearchValue(e.target.value)}
              className='h-10 border-0 bg-transparent focus-visible:ring-0 focus-visible:ring-offset-0'
            />
          </div>

          <div className='max-h-[250px] overflow-y-auto overscroll-contain'>
            <div className='p-1'>
              {filteredMerchants.length === 0 ? (
                <div className='py-6 text-center text-sm text-muted-foreground'>
                  {t('merchantPicker.noMerchantsFound')}
                </div>
              ) : (
                filteredMerchants.map((merchantName: string) => {
                  const isSelected = selectedMerchants.includes(merchantName);
                  return (
                    <div
                      key={merchantName}
                      className='relative flex cursor-pointer select-none items-center rounded-sm px-2 py-1.5 text-sm outline-none hover:bg-accent hover:text-accent-foreground'
                      onClick={() => toggleMerchant(merchantName)}
                    >
                      <Check
                        className={cn(
                          'mr-2 h-4 w-4',
                          isSelected ? 'opacity-100' : 'opacity-0'
                        )}
                      />
                      <span className='truncate'>{merchantName}</span>
                    </div>
                  );
                })
              )}
            </div>
          </div>
        </PopoverContent>
      </Popover>

      {selectedMerchants.length > 0 && (
        <div className='flex flex-wrap gap-1'>
          {selectedMerchants.map((merchantName) => (
            <Badge
              key={merchantName}
              variant='secondary'
              className='gap-1 text-xs'
            >
              {merchantName}
              <button
                type='button'
                onClick={() => removeMerchant(merchantName)}
                className='hover:bg-destructive/20 rounded-full p-0.5 ml-1'
              >
                <X className='w-3 h-3' />
              </button>
            </Badge>
          ))}
        </div>
      )}
    </div>
  );
}
